import { useSelector } from 'react-redux';
import { AccessibilityState, selectAccessibilityData } from 'reducers/accessibility';
import { selectWebskin } from 'reducers/auth';

const PRIMARY_BUTTON_BACKGROUND_COLOR = '#36705B';
const TERTIARY_BUTTON_BACKGROUND_COLOR = '#d5d5d5';
const DEFAULT_PRIMARY_COLOR = '#82c383';
const DEFAULT_GRAY = '#D5D5D5';
const DEFAULT_FOOTER_BACKGROUND_COLOR = '#f5f5f5';
const DEFAULT_PROGRESS_BAR_FOOTER_COLOR = '#731447';

const useAccessibilityColors = (): {
    enabled: boolean;
    style: React.CSSProperties;
    progressBarStyle: React.CSSProperties;
    footerStyle: React.CSSProperties;
    accessibilityData: AccessibilityState;
} => {
    const accessibilityData = useSelector(selectAccessibilityData);
    const webskin = useSelector(selectWebskin);

    const style = {
        '--backgroundColor': `${accessibilityData.backgroundColor}`,
        '--fontColor': `${accessibilityData.textColor}`,
        '--generalText-Colour': `${accessibilityData.textColor}`,
        '--titleColor': `${accessibilityData.titleColor}`,
        '--primaryButtonBackgroundColor': PRIMARY_BUTTON_BACKGROUND_COLOR,
        '--tertiaryButtonBackgroundColor': TERTIARY_BUTTON_BACKGROUND_COLOR,
        '--primaryColor': webskin?.siteColours?.find(
            (item: any) => item.code === 'SC_PrimaryButton_Background'
        )?.colourValue ?? DEFAULT_PRIMARY_COLOR,
    } as React.CSSProperties;

    const progressBarStyle = accessibilityData.isSet
        ? ({
            '--progressBarCompleteColor': accessibilityData.textColor,
            '--progressBarNoGradientColor': accessibilityData.textColor,
            '--progressBarIncompleteColor': accessibilityData.backgroundColor,
            '--progressBarBorderColor': accessibilityData.textColor,
        } as React.CSSProperties)
        : ({
            '--progressBarCompleteColor': DEFAULT_PROGRESS_BAR_FOOTER_COLOR,
            '--progressBarIncompleteColor': DEFAULT_GRAY,
            '--progressBarBorderColor': DEFAULT_GRAY,
        } as React.CSSProperties);

    const footerStyle = accessibilityData.isSet
        ? ({
            '--backgroundColor': accessibilityData.backgroundColor,
            '--footerFontColor': accessibilityData.textColor,
            '--footerBorderColor': accessibilityData.textColor,
        } as React.CSSProperties)
        : ({
            '--footerBackgroundColor': DEFAULT_FOOTER_BACKGROUND_COLOR,
            '--footerBorderColor': DEFAULT_FOOTER_BACKGROUND_COLOR,
            '--primaryButtonBackgroundColor':
                webskin?.siteColours?.find(
                    (item: any) => item.code === 'SC_PrimaryButton_Background'
                )?.colourValue ?? PRIMARY_BUTTON_BACKGROUND_COLOR,
            '--tertiaryButtonBackgroundColor': TERTIARY_BUTTON_BACKGROUND_COLOR,
        } as React.CSSProperties);

    return {
        accessibilityData,
        enabled: accessibilityData.isSet,
        style,
        progressBarStyle,
        footerStyle
    };
};

type AccessibilityOption = {
    optionText: string;
    optionId: number;
    tileColour: string;
    textColour: string;
    classStr?: string;
    isLight:boolean;
}

export const accessabilityOptions: AccessibilityOption[] = [

    {
        optionId: 1,
        optionText: 'AaBbCcDdEeFf',
        tileColour: '#000000',
        textColour: '#FFFF00',
        isLight:false,
    },
    {
        optionId: 2,
        optionText: 'AaBbCcDdEeFf',
        tileColour: '#000000',
        textColour: '#ffffff',
        isLight:false,
    },
    {
        optionId: 3,
        optionText: 'AaBbCcDdEeFf',
        tileColour: '#FFFF99',
        textColour: '#000000',
        isLight:true,
    },
];

export const addClassNamesToOptions = (
    webskin: any,
    classNames: string[]
): (AccessibilityOption)[] => {
    let defaultOption: AccessibilityOption | undefined;

    if (webskin) {
        defaultOption = {
            optionText: 'AaBbCcDdEeFf',
            optionId: 0,
            tileColour: '#ffffff',
            textColour: webskin?.textColours?.find(
                (item: any) => item.code === 'TC_General'
            )?.colourValue || '#124a57',
            classStr: classNames[0] || '',
            isLight:true,
        };
    } else {
        defaultOption = {
            optionText: 'AaBbCcDdEeFf',
            optionId: 0,
            tileColour: '#ffffff',
            textColour: '#124a57',
            classStr: classNames[0] || '',
            isLight:true,
        }
    }

    return [
        ...(defaultOption
            ? [defaultOption]
            : []),
        ...accessabilityOptions.map((option, index) => {
            return {
                ...option,
                classStr: classNames[index + 1] || '',
            };
        })
    ];
};

export default useAccessibilityColors;